import {Component, computed, inject, input, OnInit} from "@angular/core";
import {SessionRunVectorComponent} from "@common/components/session/session-run-vector/session-run-vector.component";
import {MatDividerModule} from "@angular/material/divider";
import {MatIconModule} from "@angular/material/icon";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatSelectModule} from "@angular/material/select";
import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {DatePipe} from "@angular/common";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {Translations} from "@translations";
import {StopPropagationDirective} from "@common/pipes/stop-propagation.directive";
import {MatButtonModule} from "@angular/material/button";
import {AutomaticRecordingSessionDialogStore} from "../../store/automatic-recording-session-dialog.store";
import {defer, filter} from "rxjs";
import {debounceTime, startWith} from "rxjs/operators";
import {SimTimeService} from "../../../../../../../core/services/sim-services/sim-time.service";
import {toLazySignal} from "ngxtension/to-lazy-signal";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {AutomaticRecordingSession} from "@models";
import {SimDurationInputComponent} from "@common/ui/sim-duration-input/sim-duration-input.component";

@Component({
	selector: "sim-schedule-entry",
	imports: [
		SessionRunVectorComponent,
		MatDividerModule,
		MatIconModule,
		MatFormFieldModule,
		MatSelectModule,
		ReactiveFormsModule,
		DatePipe,
		TranslateModule,
		MatButtonModule,
		StopPropagationDirective,
		SimDurationInputComponent,
	],
	templateUrl: "./schedule-entry.component.html",
	styleUrl: "./schedule-entry.component.scss",
	animations: [zoomInOnEnterAnimation({duration: 175}), zoomOutOnLeaveAnimation({duration: 175})],
})
export class ScheduleEntryComponent implements OnInit {
	readonly translate = inject(TranslateService);
	protected readonly T = Translations;

	protected readonly store = inject(AutomaticRecordingSessionDialogStore);

	public readonly index = input.required<number>();
	public readonly hasIntermission = input.required<boolean>();
	public readonly form = input.required<
		FormGroup<{
			runDurationInMinutes: FormControl<number>;
			intermissionDurationInMinutes: FormControl<number>;
		}>
	>();
	public readonly disabled = input<boolean>(false);

	readonly #durationsInMinutes = toLazySignal(defer(() => this.form().valueChanges.pipe(startWith(this.form().value))));

	protected readonly canDelete = computed(() => this.store.schedule().length > 1);
	protected readonly start = computed(() => computeStartTime(this.store.session(), this.index()));
	protected readonly end = computed(() => computeEndTime(this.start(), this.#durationsInMinutes()));

	ngOnInit() {
		this.observeFormChanges();
	}

	private observeFormChanges() {
		this.form()
			.controls.runDurationInMinutes.valueChanges.pipe(
				filter(() => this.form().controls.runDurationInMinutes.valid),
				debounceTime(500),
			)
			.subscribe((duration) => {
				this.store.scheduleEntryUpdated(this.index(), "run", duration);
			});

		this.form()
			.controls.intermissionDurationInMinutes.valueChanges.pipe(
				filter(() => this.form().controls.intermissionDurationInMinutes.valid),
				debounceTime(500),
			)
			.subscribe((duration) => {
				this.store.scheduleEntryUpdated(this.index(), "intermission", duration);
			});
	}
}

function computeStartTime(session: AutomaticRecordingSession | null, index: number): number | undefined {
	if (!session) {
		return undefined;
	}
	let startTime = new Date(session.startTime).getTime();

	for (let i = 0; i < index; i++) {
		const index = i * 2;
		const runEntry = session.schedule[index];
		const intermissionEntry = session.schedule[index + 1];
		if (runEntry) {
			startTime += runEntry.duration;
		}
		if (intermissionEntry) {
			startTime += intermissionEntry.duration;
		}
	}

	return startTime;
}

function computeEndTime(
	start: number | undefined,
	durations:
		| {
				runDurationInMinutes?: number;
		  }
		| undefined,
): number | undefined {
	if (start === undefined || !durations?.runDurationInMinutes) {
		return undefined;
	}
	return start + SimTimeService.fromMinutesToMilliseconds(durations.runDurationInMinutes);
}
